// src/client/renderer/vfx/items/orbital-cannon.ts
//
// Plan 17, Task 2.2 — Orbital Cannon pool.
//
// Triggered by THREE events:
//   1. ItemChargeEvent (itemId='orbital_cannon') → phase='cast':
//        Long-running charge beam visual (persists ~one round, pulse repeats indefinitely).
//        SINGLE-INSTANCE per caster — only one active charge per casterId.
//        New ItemChargeEvent for the same caster MUST release the prior charge
//        before acquiring the new one (F6 release-first contract).
//
//   2. ItemBeamFiredEvent (itemId='orbital_cannon'):
//        Handled by the board-level cinematic in main.ts. This pool intentionally returns null
//        for beam-fired events to avoid duplicate impact visuals.
//
//   3. ItemBlockedStoneSkinEvent (itemId='orbital_cannon') → phase='impact':
//        Stone-skin shimmer at target (blocked variant).
//        Discriminator: args.variant === 'blocked'.
//
// Charge key:
//   chargeMap is keyed directly by args.casterId (a string).
//   Pools for other casters are independent.
//
// Blocked variant discriminator:
//   args.variant === 'blocked'  → blocked (stone-skin shimmer)
//   args.variant === undefined  → normal explosion
//
// Pool: charge (single-instance per caster via internal Map<string, ...>).
//       Impact: one-shot, max-active = 2.
//
// GSAP: use tl.to with repeat:-1 / yoyo:true for charge pulse.
//       use tl.call(fn, undefined, absolutePos) — NOT eventCallback('onComplete').
//
// Registry tick contract (HF1):
//   pool.update(_deltaSeconds) IGNORES dt — does NOT call registry.update().
//   The caller (item-vfx-manager at Task 6.1) advances the shared registry once
//   per frame across all pools.
//
// Audio:
//   Manager fires the item cue only for phase='cast'. Blocked follow-up pulses
//   are intentionally silent.

import * as THREE from 'three';
import type { ItemVfxPool, ItemVfxPoolDeps, ItemVfxSpawnArgs } from './item-vfx-types';
import type {
  GameEvent,
  ItemChargeEvent,
  ItemBlockedStoneSkinEvent,
} from '../../../../shared/game/types';
import { disposeSkillshotGroup } from './skillshot-shared';

// ─────────────────────────────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────────────────────────────

/** Max simultaneously active impact emitters. */
const MAX_IMPACT_ACTIVE = 2;

/** Beam colour — electric blue-white for orbital laser. */
const BEAM_COLOUR = 0x88ccff;

/** Charge colour — pulsing blue for the held charge visual. */
const CHARGE_COLOUR = 0x4488ff;

/** Explosion colour — bright cyan-white core. */
const EXPLOSION_COLOUR = 0xaaddff;

/** Blocked variant colour — stone-grey shimmer. */
const BLOCKED_COLOUR = 0xccbbaa;

/** Impact animation duration (normal explosion + blocked shimmer). */
const IMPACT_LIFETIME = 0.5;

/** Charge visual pulse cycle duration (long-running). */
const CHARGE_PULSE_DURATION = 1.0;

/** Height above target where the orbital laser beam starts. */
const SKY_OFFSET_Y = 15;

// ─────────────────────────────────────────────────────────────────────────────
// Internal emitter state
// ─────────────────────────────────────────────────────────────────────────────

interface EmitterState {
  handle: ReturnType<ItemVfxPoolDeps['registry']['create']>;
  finished: boolean;
}

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Build a charge-phase visual: a pulsing ring at casterPosition.
 * Pulse repeats indefinitely (repeat: -1, yoyo: true) — finished marker fires
 * only when the pool explicitly releases it (via releaseCharge or on impact).
 * The GSAP handle is disposed on release which stops the animation without leaking.
 */
function buildChargeEmitter(
  target: { x: number; y: number; z: number },
  deps: ItemVfxPoolDeps,
  state: EmitterState,
): THREE.Group {
  const { registry } = deps;
  const group = new THREE.Group();

  // Pulsing sprite at charge position
  const chargeMaterial = new THREE.SpriteMaterial({
    color: CHARGE_COLOUR,
    transparent: true,
    opacity: 0.3,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
  });
  const chargeSprite = new THREE.Sprite(chargeMaterial);
  chargeSprite.position.set(target.x, target.y, target.z);
  chargeSprite.scale.set(0.5, 0.5, 1);
  group.add(chargeSprite);

  const handle = registry.create('orbital_cannon_charge');
  state.handle = handle;

  const tl = handle.timeline;

  // Pulsing opacity: 0.3 → 1.0 → 0.3, repeating indefinitely (yoyo).
  // repeat:-1 keeps the animation running until handle.dispose() is called.
  // After disposal, the repeat doesn't leak because the timeline is destroyed.
  tl.to(
    chargeMaterial,
    {
      opacity: 1.0,
      duration: CHARGE_PULSE_DURATION * 0.5,
      ease: 'power1.inOut',
      yoyo: true,
      repeat: -1,
    },
    0,
  );
  // Charge is "never finished" by the timeline — it's finished externally on release.
  // Long-running visual: don't set state.finished here.

  return group;
}

/**
 * Build an impact-phase visual (normal explosion or blocked shimmer).
 * Fires the finished marker at IMPACT_LIFETIME seconds via tl.call.
 */
function buildImpactEmitter(
  target: { x: number; y: number; z: number },
  isBlocked: boolean,
  deps: ItemVfxPoolDeps,
  state: EmitterState,
): THREE.Group {
  const { registry } = deps;
  const group = new THREE.Group();

  if (!isBlocked) {
    // Normal impact: beam line from sky to target + explosion sprite
    const colour = EXPLOSION_COLOUR;
    const beamMaterial = new THREE.LineBasicMaterial({
      color: BEAM_COLOUR,
      transparent: true,
      opacity: 1.0,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    });
    const skyPos = new THREE.Vector3(target.x, target.y + SKY_OFFSET_Y, target.z);
    const targetPos = new THREE.Vector3(target.x, target.y, target.z);
    const beamGeometry = new THREE.BufferGeometry().setFromPoints([skyPos, targetPos]);
    const beamLine = new THREE.Line(beamGeometry, beamMaterial);
    group.add(beamLine);

    // Fade out beam quickly
    const beamOpProxy = { value: 1.0 };
    const handle = registry.create('orbital_cannon_impact');
    state.handle = handle;
    const tl = handle.timeline;

    tl.to(
      beamOpProxy,
      {
        value: 0.0,
        duration: IMPACT_LIFETIME * 0.4,
        ease: 'power2.in',
        onUpdate: () => {
          beamMaterial.opacity = beamOpProxy.value;
        },
      },
      0,
    );

    // Explosion sprite
    const explosionMaterial = new THREE.SpriteMaterial({
      color: colour,
      transparent: true,
      opacity: 1.0,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    });
    const explosion = new THREE.Sprite(explosionMaterial);
    explosion.position.set(target.x, target.y, target.z);
    explosion.scale.set(0, 0, 1);
    group.add(explosion);

    tl.to(
      explosion.scale,
      {
        x: 2.5,
        y: 2.5,
        z: 1,
        duration: IMPACT_LIFETIME,
        ease: 'power2.out',
      },
      0,
    );

    const expOpProxy = { value: 1.0 };
    tl.to(
      expOpProxy,
      {
        value: 0.0,
        duration: IMPACT_LIFETIME,
        ease: 'power2.in',
        onUpdate: () => {
          explosionMaterial.opacity = expOpProxy.value;
        },
      },
      0,
    );

    tl.call(
      () => {
        state.finished = true;
      },
      undefined,
      IMPACT_LIFETIME,
    );
  } else {
    // Blocked: stone-skin shimmer at target
    const shimmerMaterial = new THREE.SpriteMaterial({
      color: BLOCKED_COLOUR,
      transparent: true,
      opacity: 0.0,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    });
    const shimmer = new THREE.Sprite(shimmerMaterial);
    shimmer.position.set(target.x, target.y, target.z);
    shimmer.scale.set(1.5, 1.5, 1);
    group.add(shimmer);

    const handle = registry.create('orbital_cannon_blocked');
    state.handle = handle;
    const tl = handle.timeline;

    // Shimmer in → out
    const opProxy = { value: 0.0 };
    tl.to(
      opProxy,
      {
        value: 0.8,
        duration: IMPACT_LIFETIME * 0.3,
        ease: 'power1.in',
        onUpdate: () => {
          shimmerMaterial.opacity = opProxy.value;
        },
      },
      0,
    );
    tl.to(
      opProxy,
      {
        value: 0.0,
        duration: IMPACT_LIFETIME * 0.7,
        ease: 'power1.out',
        onUpdate: () => {
          shimmerMaterial.opacity = opProxy.value;
        },
      },
      IMPACT_LIFETIME * 0.3,
    );

    tl.call(
      () => {
        state.finished = true;
      },
      undefined,
      IMPACT_LIFETIME,
    );
  }

  return group;
}

// ─────────────────────────────────────────────────────────────────────────────
// Factory — createOrbitalCannonPool
// ─────────────────────────────────────────────────────────────────────────────

export function createOrbitalCannonPool(deps: ItemVfxPoolDeps): ItemVfxPool {
  const { scene } = deps;

  /**
   * Charge Map key: args.casterId (direct string).
   * One active charge emitter per caster.
   */
  const chargeMap = new Map<string, { emitter: THREE.Object3D; state: EmitterState }>();

  /**
   * Impact emitter set — one-shot, max-active = MAX_IMPACT_ACTIVE.
   */
  const impactStates = new Map<THREE.Object3D, EmitterState>();

  let disposed = false;

  // ── acquire ──────────────────────────────────────────────────────────────

  function acquire(args: ItemVfxSpawnArgs): THREE.Object3D | null {
    if (disposed) return null;

    const { phase, targetPosition, casterPosition } = args;
    const isBlocked = args.variant === 'blocked';

    if (phase === 'cast') {
      // ── Charge phase ─────────────────────────────────────────────────────
      const chargeKey = args.casterId ?? '';
      const anchor = targetPosition ?? casterPosition ?? { x: 0, y: 0, z: 0 };

      // F6: release-first — if a prior charge exists for this casterId, release it
      const prior = chargeMap.get(chargeKey);
      if (prior) {
        prior.state.handle.dispose();
        scene.remove(prior.emitter);
        disposeSkillshotGroup(prior.emitter);
        chargeMap.delete(chargeKey);
      }

      const state: EmitterState = { handle: null!, finished: false };
      const group = buildChargeEmitter(anchor, deps, state);
      scene.add(group);
      chargeMap.set(chargeKey, { emitter: group, state });
      return group;
    } else {
      // ── Impact phase ─────────────────────────────────────────────────────
      if (impactStates.size >= MAX_IMPACT_ACTIVE) return null;

      // F6 charge-release-on-impact: if this caster has a held charge, release it first.
      const chargeKey = args.casterId ?? '';
      const priorCharge = chargeMap.get(chargeKey);
      if (priorCharge) {
        priorCharge.state.handle.dispose();
        scene.remove(priorCharge.emitter);
        disposeSkillshotGroup(priorCharge.emitter);
        chargeMap.delete(chargeKey);
      }

      const target = targetPosition ?? { x: 0, y: 0, z: 0 };
      const state: EmitterState = { handle: null!, finished: false };
      const group = buildImpactEmitter(target, isBlocked, deps, state);
      scene.add(group);
      impactStates.set(group, state);
      return group;
    }
  }

  // ── update ───────────────────────────────────────────────────────────────
  // Registry tick contract (HF1): pool.update does NOT advance the registry.

  function update(_deltaSeconds: number): ReadonlySet<THREE.Object3D> {
    const all = new Set<THREE.Object3D>();
    for (const { emitter } of chargeMap.values()) {
      all.add(emitter);
    }
    for (const emitter of impactStates.keys()) {
      all.add(emitter);
    }
    return all;
  }

  // ── isFinished ───────────────────────────────────────────────────────────

  function isFinished(emitter: THREE.Object3D): boolean {
    // Check charge map
    for (const { emitter: ce, state } of chargeMap.values()) {
      if (ce === emitter) return state.finished;
    }
    // Check impact set
    const impactState = impactStates.get(emitter);
    if (impactState) return impactState.finished;
    // Unknown emitter
    return true;
  }

  // ── release ──────────────────────────────────────────────────────────────

  function release(emitter: THREE.Object3D): void {
    // Check charge map first
    for (const [key, { emitter: ce, state }] of chargeMap.entries()) {
      if (ce === emitter) {
        state.handle.dispose();
        scene.remove(emitter);
        disposeSkillshotGroup(emitter);
        chargeMap.delete(key);
        return;
      }
    }
    // Check impact set
    const impactState = impactStates.get(emitter);
    if (impactState) {
      impactState.handle.dispose();
      impactStates.delete(emitter);
      scene.remove(emitter);
      disposeSkillshotGroup(emitter);
    }
  }

  // ── dispose (F4) ─────────────────────────────────────────────────────────

  function dispose(): void {
    if (disposed) return;
    disposed = true;

    // Dispose all charge emitters
    for (const { emitter, state } of chargeMap.values()) {
      state.handle.dispose();
      scene.remove(emitter);
      disposeSkillshotGroup(emitter);
    }
    chargeMap.clear();

    // Dispose all impact emitters
    for (const [emitter, state] of impactStates) {
      state.handle.dispose();
      scene.remove(emitter);
      disposeSkillshotGroup(emitter);
    }
    impactStates.clear();
  }

  return { acquire, update, isFinished, release, dispose };
}

// ─────────────────────────────────────────────────────────────────────────────
// Event resolver — resolveOrbitalCannonForEvent
// ─────────────────────────────────────────────────────────────────────────────

export function resolveOrbitalCannonForEvent(
  event: GameEvent,
  getPositionForPlayer?: (playerId: string) => { x: number; y: number; z: number } | undefined,
): { poolId: 'orbital_cannon'; args: ItemVfxSpawnArgs } | null {
  if (
    event.type !== 'item_charge' &&
    event.type !== 'item_beam_fired' &&
    event.type !== 'item_blocked_stoneskin'
  ) {
    return null;
  }

  // All three event types have itemId; check it's orbital_cannon
  if (event.itemId !== 'orbital_cannon') return null;

  if (event.type === 'item_charge') {
    const charge = event as ItemChargeEvent;
    return {
      poolId: 'orbital_cannon',
      args: {
        phase: 'cast',
        casterId: charge.casterId,
        casterPosition: getPositionForPlayer?.(charge.casterId),
        targetPosition: getPositionForPlayer?.(charge.targetId),
      },
    };
  }

  if (event.type === 'item_beam_fired') {
    return null;
  }

  // item_blocked_stoneskin with itemId === 'orbital_cannon'
  const blocked = event as ItemBlockedStoneSkinEvent;
  return {
    poolId: 'orbital_cannon',
    args: {
      phase: 'impact',
      casterId: blocked.casterId,
      targetPosition: getPositionForPlayer?.(blocked.blockerId),
      variant: 'blocked',
    },
  };
}
